import React, { useCallback } from "react";
import { useRouter } from "next/router";
import { object, array } from "prop-types";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import NextLink from "@insightfulscience/smart-react/NextLink";
import Html from "@insightfulscience/smart-react/Html";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import HeroAuthor from "@insightfulscience/atomic-react/Hero/Author";
import HeroAction from "@insightfulscience/atomic-react/Hero/Action";
import { HeroCol, HeroRow } from "@insightfulscience/atomic-react/Hero/Grid";
import HeroButtonLink from "@insightfulscience/atomic-react/Hero/ButtonLink";
import Button from "@insightfulscience/atomic-react/Button";
import Section from "@insightfulscience/atomic-react/Section";
import { Container, Col, Row } from "@insightfulscience/atomic-react/Grid";
import Breadcrumbs from "@insightfulscience/atomic-react/Breadcrumbs";
import BreadcrumbItem, {
	BreadcrumbItemLink,
} from "@insightfulscience/atomic-react/Breadcrumbs/Item";
import PageNavbarSearch from "@insightfulscience/atomic-react/PageNavbarSearch";
import PageNavbarSearchMain from "@insightfulscience/atomic-react/PageNavbarSearch/Main";
import PageNavbarSearchSidebar from "@insightfulscience/atomic-react/PageNavbarSearch/Sidebar";
import LandingSimpleSection from "@insightfulscience/atomic-react/LandingElement/SimpleSection";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import Image from "@insightfulscience/atomic-react/Image";
import UList from "@insightfulscience/atomic-react/UList";
import Link from "@insightfulscience/atomic-react/Link";

import { PageNavbarSearchLink } from "@src/Plasmids/ui/PageNavbarSearch";
import BlockAutocomplete from "@src/Plasmids/ui/BlockAutocomplete";
import { EXPLORE_OVER } from "@src/Plasmids/constants";
import {
	ROUTE_DOMAIN_PROD,
	ROUTE_FREE_TRIAL,
	ROUTE_HOME,
	ROUTE_PLASMIDS,
	ROUTE_SNAPGENE_VIEWER,
} from "@src/Plasmids/routes";
import PlasmidInteractiveMap from "@src/Plasmids/ui/PlasmidInteractiveMap";
import PlasmidStaticMap from "@src/Plasmids/ui/PlasmidStaticMap";
import SectionCopyright from "@src/Plasmids/ui/SectionCopyright";
import SectionItems from "@src/Plasmids/ui/SectionItems";
import PlasmidSequenceViewer from "@src/Plasmids/ui/PlasmidSequenceViewer";
import PlasmidLegacyZone from "@src/Plasmids/ui/PlasmidsLegacyZone";
import {
	correctPlasmidName,
	routePlasmid,
	routeSet,
} from "@src/Plasmids/utils/plasmid/helpers";
import sgApiConnection from "@src/Plasmids/services/sgApiConnection";
import sgApiClient from "@src/Plasmids/services/sgApiClient";
import SiteLayout from "@src/SiteLayout";
import SectionTryForFree from "@src/SectionTryForFree";
import { publicConfig } from "@src/config";
import WithClientSideServices from "@src/WithClientSideServices";
import { encodeURLFragment } from "@src/Plasmids/utils/encodeURLFragment";
import resources from "../../../locales/plasmids-plasmid-page";
import { setProtocol } from "../../../src/Plasmids/actions";

const PlasmidsPlasmidPage = ({ sets = [], plasmid, setSequencesData = {} }) => {
	const [t] = useLocale("plasmids-plasmid");

	const router = useRouter();
	const { format } = router.query;

	const siteDomain = publicConfig.STATIC_SITE_DOMAIN;

	const { sequences } = setSequencesData;
	const {
		setID,
		plasmidID,
		plasmidName,
		plasmidDesc,
		plasmidAuthor,
		plasmidFilename,
	} = plasmid;
	const set = sets.filter((item) => item.set === setID)[0];
	const { plasmidSetName } = set;

	const title = correctPlasmidName(plasmid);
	const setTitle = plasmidSetName;
	const description = plasmidDesc;
	const author = plasmidAuthor;
	const filename = plasmidFilename;

	const filePath = `${siteDomain}/local/fetch.php?set=${setID}&plasmid=${plasmidID}`;

	const downloadFile = useCallback(() => {
		const fileAnchor = document.createElement("a");
		document.body.appendChild(fileAnchor);
		fileAnchor.href = `https://${filePath}`;
		fileAnchor.click();
		document.body.removeChild(fileAnchor);
	}, [filePath]);

	const route = routePlasmid(setID, plasmidID);
	const url = `https://${ROUTE_DOMAIN_PROD}${route}`;

	return (
		<SiteLayout hero isBorderBottomNone>
			<PageMeta title={t("META_TITLE", { title })} description={description}>
				<PageMeta.OGP
					title={t("META_TITLE", { title })}
					description={description}
					siteName={t("META_SITE_NAME")}
					articlePublisher={t("META_ARTICLE_PUBLISHER")}
					locale={t("META_LOCALE")}
					type={t("META_TYPE")}
					url={url}
					image={t("META_IMAGE")}
					imageSecureUrl={t("META_IMAGE_SECURE")}
					isImageBaseUrl
				/>
				<PageMeta.Canonical href={url} />
				<PageMeta.Author content={t("META_AUTHOR")} />
				<PageMeta.Twitter
					title={t("META_TITLE", { title })}
					description={description}
					card={t("META_TWITTER_CARD")}
					creator={t("META_TWITTER_CREATOR")}
					site={t("META_TWITTER_SITE")}
					image={t("META_TWITTER_IMAGE")}
					isImageBaseUrl
				/>
				<PageMeta.Robots content={t("META_ROBOTS")} />
				<PageMeta.Custom>
					<link
						href="https://fonts.googleapis.com/css2?family=Roboto+Mono&display=swap"
						rel="preload"
						as="style"
					/>
					<link
						href="https://fonts.googleapis.com/css2?family=Roboto+Mono&display=swap"
						rel="stylesheet"
					/>
				</PageMeta.Custom>
			</PageMeta>
			<Hero>
				<HeroTitle dangerouslySetInnerHTML={{ __html: title }} />
				<HeroDescription>{description}</HeroDescription>
				{author ? (
					<HeroAuthor>
						{t("SEQUENCE_AUTHOR")}: {author}
					</HeroAuthor>
				) : null}
				<HeroAction>
					<HeroRow>
						<HeroCol>
							<Button
								size="s"
								onClick={() =>
									setProtocol("snapgene", { plasmidName, filePath })
								}
							>
								{t("OPEN_IN_SNAPGENE")}
							</Button>
						</HeroCol>
						<HeroCol>
							<NextLink
								Component={HeroButtonLink}
								href={ROUTE_SNAPGENE_VIEWER}
								styleType="secondary"
								isOutline2
							>
								{t("TRY_SNAPGENE_FOR_FREE")}
							</NextLink>
						</HeroCol>
					</HeroRow>
				</HeroAction>
				<HeroRow spacing={{ mt: 2 }}>
					<HeroCol sx={{ textAlign: "center" }}>
						<Link.Button onClick={() => downloadFile()} target="_blank">
							{t("DOWNLOAD_PLASMID")}
						</Link.Button>
					</HeroCol>
				</HeroRow>
			</Hero>
			<PageNavbarSearch>
				<PageNavbarSearchMain>
					Explore Over {EXPLORE_OVER} Plasmids:{" "}
					<NextLink Component={PageNavbarSearchLink} href={routeSet(setID)}>
						<Html Tag="span">{setTitle}</Html>
					</NextLink>{" "}
					|{" "}
					<NextLink Component={PageNavbarSearchLink} href={ROUTE_PLASMIDS}>
						{t("MORE_PLASMID_SETS")}
					</NextLink>
				</PageNavbarSearchMain>
				<PageNavbarSearchSidebar>
					<BlockAutocomplete />
				</PageNavbarSearchSidebar>
			</PageNavbarSearch>
			<Section spacing={{ py: 4 }}>
				<Container>
					<Row>
						<Col>
							<Breadcrumbs>
								<BreadcrumbItem>
									<NextLink Component={BreadcrumbItemLink} href={ROUTE_HOME}>
										{t("HOME")}
									</NextLink>
								</BreadcrumbItem>
								<BreadcrumbItem>
									<NextLink
										Component={BreadcrumbItemLink}
										href={ROUTE_PLASMIDS}
									>
										{t("PLASMIDS")}
									</NextLink>
								</BreadcrumbItem>
								<BreadcrumbItem>
									<NextLink
										Component={BreadcrumbItemLink}
										href={routeSet(setID)}
									>
										<Html Tag="span">{setTitle}</Html>
									</NextLink>
								</BreadcrumbItem>
								<BreadcrumbItem>{title}</BreadcrumbItem>
							</Breadcrumbs>
						</Col>
					</Row>
				</Container>
			</Section>
			<LandingSimpleSection isBorderNone>
				<LandingSimpleSection.Body>
					<Paragraph Tag="div">
						<NextLink href={format === "png" ? route : `${route}?format=png`}>
							{format === "png"
								? t("SHOW_INTERACTIVE_MAP")
								: t("SHOW_STATIC_MAP")}
						</NextLink>
					</Paragraph>
					<PlasmidLegacyZone>
						{format === "png" ? (
							<PlasmidStaticMap
								key={`static-image/${setID}/${plasmidID}`}
								siteDomain={siteDomain}
								setID={setID}
								plasmidID={plasmidID}
							/>
						) : (
							<PlasmidInteractiveMap
								key={`interactive-image/${setID}/${plasmidID}`}
								siteDomain={siteDomain}
								setID={setID}
								plasmidID={plasmidID}
								plasmidFilename={plasmidFilename}
							/>
						)}
						<PlasmidSequenceViewer
							key={`viewer/${setID}/${plasmidID}`}
							setID={setID}
							plasmidID={plasmidID}
							plasmidFilename={plasmidFilename}
						/>
					</PlasmidLegacyZone>
				</LandingSimpleSection.Body>
			</LandingSimpleSection>
			<LandingSimpleSection isBorderNone bgColor="black--02">
				<LandingSimpleSection.TitleV2>
					Download {filename} file
				</LandingSimpleSection.TitleV2>
				<LandingSimpleSection.Body>
					<Row>
						<Col sm={12} sx={{ textAlign: "center" }} spacing={{ mt: 5 }}>
							<Button
								spacing={{ m: 3 }}
								size="s"
								onClick={() => downloadFile()}
							>
								{t("DOWNLOAD_PLASMID")}
							</Button>
							<Button
								spacing={{ m: 3 }}
								size="s"
								styleType="secondary"
								isOutline2
								onClick={() =>
									setProtocol("snapgene", { plasmidName, filePath })
								}
							>
								{t("OPEN_IN_SNAPGENE")}
							</Button>
						</Col>
					</Row>
					<Row my mx utility={{ justify: "center" }} spacing={{ mt: 7 }}>
						<Col
							sm={12}
							md={11}
							lg={5}
							spacing={{ py: 5, px: 10 }}
							utility={{ bgColor: "white", border: { width: 0 } }}
							className="card-block"
						>
							<Paragraph
								sx={{
									fontSize: "xxl",
									fontWeight: "normal",
									fontColor: "black",
									textAlign: "center",
								}}
								utility={{ md: { justify: "center" } }}
							>
								<Image
									src="/images/snapgene/plasmid-details/snapgene.png"
									spacing={{ mr: 2 }}
									sizing={{ maxW: 7 }}
								/>
								SnapGene
							</Paragraph>
							<Paragraph sx={{ fontSize: "l", textAlign: "center" }}>
								SnapGene is the easiest way to plan, visualize and document your
								everyday molecular biology procedures
							</Paragraph>
							<UList spacing={{ mb: 15 }}>
								<UList.Item>
									Fast accurate construct design for all major molecular cloning
									techniques
								</UList.Item>
								<UList.Item>
									Validate sequenced constructs using powerful alignment tools
								</UList.Item>
								<UList.Item>
									Customize plasmid maps with flexible annotation and
									visualization controls
								</UList.Item>
								<UList.Item>
									Automatically generate a rich graphical history of every edit
									and procedure
								</UList.Item>
							</UList>
							<Row>
								<Col sm={12} sx={{ textAlign: "center" }}>
									<NextLink
										Component={Button.Link}
										href={ROUTE_FREE_TRIAL}
										className="mt-3 width-100"
										size="s"
										styleType="secondary"
										isOutline2
									>
										{t("DOWNLOAD_SNAPGENE")}
									</NextLink>
								</Col>
							</Row>
						</Col>
						<Col
							sm={12}
							md={11}
							lg={5}
							spacing={{ py: 5, px: 10 }}
							utility={{ bgColor: "white", border: { width: 0 } }}
							className="card-block"
						>
							<Paragraph
								sx={{
									fontSize: "xxl",
									fontWeight: "normal",
									fontColor: "black",
									textAlign: "center",
								}}
								utility={{ md: { justify: "center" } }}
							>
								<Image
									src="/images/snapgene/plasmid-details/snapgene-viewer.png"
									spacing={{ mr: 2 }}
									sizing={{ maxW: 7 }}
								/>
								{t("SNAPGENE_VIEWER")}
							</Paragraph>
							<Paragraph sx={{ fontSize: "l", textAlign: "center" }}>
								SnapGene Viewer is free software that allows molecular
								biologists to create, browse, and share richly annotated
								sequence files.
							</Paragraph>
							<UList spacing={{ mb: 27 }}>
								<UList.Item>
									Gain unparalleled visibility of your plasmids, DNA and protein
									sequences
								</UList.Item>
								<UList.Item>
									Annotate features on your plasmids using the curated feature
									database
								</UList.Item>
								<UList.Item>
									Store, search, and share your sequences, files and maps
								</UList.Item>
							</UList>
							<Row>
								<Col sm={12} sx={{ textAlign: "center" }}>
									<NextLink
										Component={Button.Link}
										href={ROUTE_SNAPGENE_VIEWER}
										className="mt-3 width-100"
										size="s"
										styleType="secondary"
										isOutline2
									>
										{t("DOWNLOAD_SNAPGENE_VIEWER")}
									</NextLink>
								</Col>
							</Row>
						</Col>
					</Row>
				</LandingSimpleSection.Body>
			</LandingSimpleSection>
			<SectionItems
				type="sequence"
				title={t("INDIVIDUAL_SEQUENCES_MAPS")}
				setID={setID}
				plasmidID={plasmidID}
				items={sequences}
			/>
			<SectionItems
				type="set"
				title={t("PLASMID_SETS")}
				setID={setID}
				items={sets}
			/>
			<SectionCopyright />
			<SectionTryForFree />
		</SiteLayout>
	);
};

PlasmidsPlasmidPage.propTypes = {
	sets: array.isRequired,
	setSequencesData: object.isRequired,
	plasmid: object.isRequired,
};

export const getStaticPaths = async () => {
	/*
		We do not pregenerate pages for plasmids, as there is roughly a thousand of them. It would cause:
		a. Thousands of API calls to SG API.
		b. Way too big bundle size.
		c. Way too long deployment time.

		We just allow them to be rendered when needed and added to cache.

		TODO: we may pick the most often loaded plasmids and prerender them.
	*/
	return {
		paths: [],
		fallback: "blocking",
	};
};

export const getStaticProps = async ({ params: { setID, plasmidID } }) => {
	const apiConn = sgApiConnection(global.fetch, publicConfig.SG_API_BASE_URL);
	const apiClient = sgApiClient(apiConn);

	const sets = await apiClient.getSets();
	const setSequencesData = await apiClient.getSequences(
		encodeURLFragment(setID),
	);
	const plasmid = await apiClient.getPlasmid(
		encodeURLFragment(setID),
		encodeURLFragment(plasmidID),
	);

	return sets && setSequencesData && plasmid
		? {
				props: { sets, setSequencesData, plasmid },
				revalidate: false,
			}
		: { notFound: true };
};

export default pipe(
	PlasmidsPlasmidPage,
	WithLocale({ resources }),
	WithClientSideServices({
		sgApiConn: () =>
			sgApiConnection(global.fetch, publicConfig.SG_API_BASE_URL),
		sgApiClient: ({ sgApiConn }) => sgApiClient(sgApiConn),
	}),
);
